'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import BrandFigure from '@/app/components/blog/BrandFigure'

export default function Post23Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Salt: Should You Switch Brands?"
      author="MicroPlastics Team"
      date="September 25, 2025"
      readTime="6 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app scanning salt products"
    >
      <BlogTLDR>
        Sea salt contains up to <strong>600 microplastic particles per
        kilogram</strong>, the highest of any common dietary salt, because
        ocean evaporation concentrates plastic along with salt. Rock salt and
        mined Himalayan salt consistently test cleaner. Switching from sea
        salt to rock or Himalayan is a cheap, simple swap for anyone who
        cooks daily.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Sea salt: up to 600 microplastic particles per kg, highest among dietary salts.</>,
          <>Rock salt and mined Himalayan salt: lowest, predate modern plastic pollution.</>,
          <>Plastic content mirrors source ocean. Mediterranean and Asian sea salts test highest.</>,
          <>2026 context: salt is high in <em>concentration</em> but low in <em>contribution</em>, you eat only a few grams a day, so switching salt is a cheap swap but a small lever.</>,
        ]}
      />

      {/* Introduction */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Salt on Your Table May Contain Plastic
      </h2>
      <p>
        Salt is one of the most universal ingredients in the human diet. We sprinkle it on eggs
        in the morning, season soups and sauces at dinner, and consume it in virtually every
        packaged food on grocery store shelves. It is so fundamental that we rarely think about
        what else might be hiding in those familiar white crystals. But a growing body of research
        has revealed an unsettling truth: the salt you use every day very likely contains
        microplastics.
      </p>
      <p>
        A landmark global study published in <em>Environmental Science &amp; Technology</em> found
        that <strong>90% of sea salt brands tested worldwide contain microplastics</strong>. The
        researchers analyzed 39 salt brands from 21 countries across six continents, and the results
        were striking. Only three brands were entirely free of microplastic contamination. That
        means the vast majority of salt consumed around the world carries tiny fragments of plastic
        directly into our bodies with every meal.
      </p>
      <p>
        If you have been reading about microplastics in other parts of your diet, this may not come
        as a complete surprise. Our guide on{' '}
        <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:underline font-medium">
          microplastics in food
        </Link>{' '}
        covers the broader picture of how plastic particles have infiltrated everything from
        seafood to rice. But salt deserves its own closer look because of how universally and
        frequently it is consumed, and because the type of salt you choose can make a meaningful
        difference in your exposure.
      </p>

      {/* Global Salt Study */}
      <BrandFigure
        src="/social/stat-salt.webp"
        alt="Stat card: 90% of table salt brands tested contained microplastics, across 39 brands from 21 countries, with sea salt highest. Source: Environmental Science and Technology, 2018."
        width={900}
        height={1125}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Context: High Concentration, Small Total Contribution
      </h2>
      <p>
        Salt reliably appears near the top of any &quot;most contaminated foods&quot; list,
        and per gram that ranking is deserved -- sea salt is literally evaporated seawater,
        so whatever was suspended in the water ends up in the crystal.
      </p>
      <p>
        A 2026 University of Amsterdam study in the <em>Journal of Hazardous Materials</em>{' '}
        put that in proportion. Measuring across 13 food categories including salt, sugar,
        honey, grains, produce, meat, shellfish and beverages, it found that{' '}
        <strong>fruit, vegetables and grains together contribute roughly 99.5% of daily
        microplastic particle intake</strong>. Salt did not dominate, for a simple reason:
        you eat a few grams of it a day.
      </p>
      <p>
        This is the distinction that most coverage collapses.{' '}
        <strong>Concentration</strong> (particles per gram) and{' '}
        <strong>contribution</strong> (particles per day from that food) are different
        questions, and salt scores high on the first while scoring low on the second. A food
        eaten by the gram cannot outweigh foods eaten by the hundreds of grams, however
        contaminated it is.
      </p>
      <p>
        The practical consequence is reassuring: switching salt is one of the cheapest swaps
        available -- rock salt and well-sourced mined salt avoid the marine contamination
        route entirely -- but it is a small lever, and it should not be mistaken for a
        meaningful reduction in total exposure.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the Global Salt Study Found
      </h2>
      <p>
        The most comprehensive study on microplastics in salt to date was led by researchers from
        South Korea and Greenpeace East Asia. They collected salt samples from across the globe and
        used micro-Fourier transform infrared spectroscopy to identify and count plastic particles
        in each sample. The findings painted a clear picture of widespread contamination.
      </p>
      <p>
        Out of the 39 brands tested, <strong>36 contained microplastics</strong>. The density of
        plastic particles ranged dramatically, from zero in the cleanest samples to over{' '}
        <strong>1,674 microplastic particles per kilogram</strong> in the most contaminated sea
        salt brands. The types of plastic identified included polyethylene (the most common plastic
        in the world, used in bags and packaging), polypropylene (found in bottle caps and food
        containers), and polyethylene terephthalate (PET, used in water bottles and food
        packaging). These are the same plastics that dominate ocean pollution, and their presence
        in salt is a direct consequence of the plastic waste crisis in our waterways.
      </p>
      <p>
        The study also found a strong correlation between the level of microplastic contamination
        in salt and the level of plastic pollution in the waters where the salt was harvested. In
        other words, the more polluted the ocean or lake, the more microplastics end up in the
        salt produced from it.
      </p>

      {/* Sea Salt vs Rock Salt vs Himalayan */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Sea Salt vs Rock Salt vs Himalayan Salt: Which Is Safest?
      </h2>
      <p>
        Salts differ widely in microplastic contamination. The source of
        the salt and the method used to produce it have a profound impact on how many plastic
        particles end up in the final product.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Sea Salt: The Highest Microplastic Levels
      </h3>
      <p>
        Sea salt consistently shows the highest microplastic contamination of any salt type.
        Studies have detected anywhere from <strong>0 to 1,674 microplastic particles per
        kilogram</strong> of sea salt, with most brands falling in the range of 600 to 1,800
        particles per kilogram. Some Asian sea salt brands have tested at concentrations exceeding{' '}
        <strong>13,000 particles per kilogram</strong> in separate analyses. This makes sense given
        that sea salt is produced by evaporating seawater in open pans or shallow pools. As the
        water evaporates, every contaminant in the water -- including microplastics -- becomes
        concentrated in the remaining salt crystals.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Lake Salt: A Middle Ground
      </h3>
      <p>
        Salt harvested from inland lakes shows moderate contamination levels, averaging around{' '}
        <strong>350 microplastic particles per kilogram</strong>. While lower than sea salt, lake
        salt still reflects the microplastic pollution present in freshwater systems. Lakes receive
        plastic contamination from rivers, stormwater runoff, wastewater discharge, and atmospheric
        deposition. The level of contamination varies significantly depending on how industrialized
        and populated the surrounding area is.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Rock Salt and Himalayan Salt: The Cleanest Options
      </h3>
      <p>
        Rock salt, including Himalayan pink salt, consistently shows the lowest microplastic
        contamination. Studies have found rock salt to contain roughly{' '}
        <strong>50 to 280 microplastic particles per kilogram</strong> -- significantly less than
        sea salt. The reason is straightforward: rock salt is mined from ancient underground
        deposits that formed millions of years ago, long before plastic was ever invented. These
        deep geological formations have had virtually no exposure to modern plastic pollution.
        Himalayan salt, mined from the Khewra Salt Mine in Pakistan, comes from deposits that are
        approximately 250 million years old and is among the least contaminated salt options
        available.
      </p>
      <p>
        That said, even rock salt is not completely free of microplastics. Small amounts can be
        introduced during the mining, processing, and packaging stages. But the baseline
        contamination from the salt deposit itself is dramatically lower than what is found in
        ocean-derived products.
      </p>

      {/* Geographic Differences */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Geographic Differences: Why Location Matters
      </h2>
      <p>
        Where your salt comes from matters almost as much as what type it is. The global study
        revealed stark geographic differences in contamination levels, and these differences map
        closely to regional ocean pollution patterns.
      </p>
      <p>
        <strong>Asian sea salts showed the highest microplastic concentrations</strong>,
        particularly those from Indonesia, the Philippines, and China. This aligns with data
        showing that Asian rivers and coastal waters receive some of the highest volumes of plastic
        waste in the world. Indonesia alone is estimated to discharge over 600,000 metric tons of
        plastic into the ocean annually. Salt produced by evaporating these heavily polluted
        coastal waters naturally concentrates the plastic particles present in them.
      </p>
      <p>
        European and North American sea salts showed lower levels on average, though contamination
        was still present in the vast majority of samples. Salt from remote oceanic regions, such
        as certain brands harvested from less industrialized coastlines, tended to have the lowest
        sea salt contamination levels -- but even these were not zero.
      </p>
      <p>
        The takeaway is clear: if you do prefer sea salt, sourcing it from regions with cleaner
        ocean water can reduce your exposure. However, no sea salt on the market can guarantee zero
        microplastic content given the global scale of ocean plastic pollution.
      </p>

      {/* How Microplastics Get Into Salt */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Microplastics Get Into Salt
      </h2>
      <p>
        Understanding the contamination pathway helps explain why sea salt is so much more affected
        than mined salt. The process is actually quite straightforward.
      </p>
      <p>
        Every year, an estimated <strong>8 to 12 million metric tons of plastic waste</strong>{' '}
        enter the world&apos;s oceans. Over time, sunlight, wave action, and microbial activity
        break this plastic down into increasingly smaller fragments. These microplastic particles,
        measuring less than 5 millimeters in diameter, become suspended in seawater at every depth
        and in every ocean basin that has been studied.
      </p>
      <p>
        When sea salt is produced, seawater is pumped into large, shallow evaporation ponds.
        As the sun and wind evaporate the water over a period of weeks or months, the salt
        crystallizes and is harvested. But the microplastics do not evaporate. They become
        trapped within and between the salt crystals, concentrating as the water volume decreases.
        The result is a product that directly reflects the microplastic burden of the source water.
      </p>
      <p>
        Additional contamination can occur during processing and packaging. Salt that is ground,
        sieved, or packaged using plastic equipment and containers may pick up extra particles.
        A 2026 controlled study now gives that last contact point its own measurement: see{' '}
        <Link href="/blog/do-plastic-salt-grinders-shed-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how many particles three plastic salt-grinder heads released
        </Link>{' '}
        while grinding 50 g of salt. This is why the contamination levels in rock salt, while much
        lower than sea salt, are not zero -- the mining and processing chain introduces some degree
        of plastic contact.
      </p>

      {/* Health Impact */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Health Impact: How Much Microplastic Are You Getting From Salt?
      </h2>
      <p>
        To put the numbers in perspective, let us consider how much salt the average person
        actually consumes and what that means for microplastic intake.
      </p>
      <p>
        The average global salt intake is approximately <strong>10 grams per day</strong>, though
        health organizations recommend no more than 5 to 6 grams. At 10 grams per day, if you are
        using a typical sea salt with around 600 microplastic particles per kilogram, you would
        ingest roughly <strong>6 microplastic particles per day</strong> from salt alone. Over the
        course of a year, that adds up to approximately <strong>2,190 particles</strong>. If you
        happen to use one of the more contaminated brands with 1,800 particles per kilogram, your
        annual intake from salt alone jumps to over <strong>6,500 particles</strong>.
      </p>
      <p>
        On its own, that number might sound alarming -- or it might sound manageable. The context
        matters. Researchers estimate that the average person ingests between{' '}
        <strong>74,000 and 121,000 microplastic particles per year</strong> from all dietary
        sources combined, plus an additional significant amount through inhalation. Salt
        contributes a relatively small fraction of your total microplastic exposure compared to
        sources like bottled water, seafood, and airborne particles. However, because salt
        consumption is so consistent and lifelong, even a small daily dose accumulates
        significantly over decades.
      </p>

      {/* Is It Dangerous? */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Is the Amount Dangerous?
      </h2>
      <p>
        The honest answer is that scientists are still working to determine the precise health
        effects of chronic low-level microplastic ingestion. What we do know is that microplastic
        particles can carry harmful chemical additives such as phthalates, bisphenol A, and flame
        retardants. These chemicals can leach from the plastic surface into body tissues after
        ingestion. Microplastics have also been shown to act as carriers for heavy metals and
        persistent organic pollutants that adsorb onto their surfaces in contaminated water.
      </p>
      <p>
        Laboratory studies have demonstrated that microplastics can cause gut inflammation,
        disrupt the gut microbiome, trigger oxidative stress, and interfere with endocrine
        function. Nanoplastics -- the smallest particles -- are capable of crossing cell membranes
        and have been detected in human blood, lung tissue, and placental tissue.
      </p>
      <p>
        While the microplastic dose from salt alone is unlikely to cause acute harm, the
        precautionary principle applies. Every source of microplastic exposure adds to your
        cumulative body burden. Reducing intake where it is easy to do so -- and switching salt
        brands is one of the easiest changes you can make -- is a sensible step toward lowering
        your overall exposure. For a broader look at minimizing microplastics across your entire
        lifestyle, see our comprehensive guide on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      {/* Best Salt Choices */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Best Salt Choices for Minimizing Microplastics
      </h2>
      <p>
        Based on the available research, here are the best options ranked from lowest to highest
        typical microplastic contamination:
      </p>
      <ol className="list-decimal pl-6 space-y-3">
        <li>
          <strong>Himalayan pink salt</strong> -- Mined from ancient geological deposits with
          minimal exposure to modern pollution. Consistently among the lowest contamination levels
          tested. Choose brands that package in glass jars or paper bags rather than plastic.
        </li>
        <li>
          <strong>Mined rock salt</strong> -- Similar to Himalayan salt in that it comes from
          underground deposits. Look for brands that are minimally processed and avoid those
          packaged in plastic grinder mechanisms.
        </li>
        <li>
          <strong>Refined table salt</strong> -- While often sourced from sea salt or rock salt,
          the refining process can remove some microplastic particles through filtration and
          washing. However, the degree of removal varies by manufacturer and is rarely disclosed.
        </li>
        <li>
          <strong>Sea salt from cleaner regions</strong> -- If you strongly prefer sea salt for its
          flavor and mineral content, choose brands sourced from less polluted waters. European
          Atlantic sea salts and those from remote Pacific islands tend to have lower contamination
          than Asian coastal varieties.
        </li>
      </ol>
      <p>
        Regardless of which salt you choose, storing it in glass containers and choosing a refillable
        ceramic- or steel-burr grinder removes the directly measured plastic-abrasion step.
      </p>

      {/* How Much Salt Should You Eat */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Much Salt Should You Be Eating?
      </h2>
      <p>
        Setting aside the microplastics question, it is worth noting that most people consume far
        more salt than is recommended for good health. The World Health Organization recommends a
        maximum of <strong>5 grams of salt per day</strong> (about one teaspoon), while the
        American Heart Association suggests an even lower ideal limit of{' '}
        <strong>3.75 grams per day</strong> (1,500 milligrams of sodium). The global average
        intake is roughly double the WHO recommendation.
      </p>
      <p>
        Reducing your overall salt intake has a dual benefit in the context of microplastics. Not
        only does lower salt consumption reduce your risk of high blood pressure, heart disease,
        and stroke, but it also directly reduces the number of microplastic particles you ingest
        from salt. If you cut your salt intake from 10 grams to the recommended 5 grams per day,
        you automatically halve your salt-related microplastic exposure.
      </p>
      <p>
        Most dietary salt does not come from the shaker on your table -- it comes from processed
        and packaged foods. Cooking more meals at home with fresh ingredients and seasoning with
        herbs, spices, citrus, and vinegar instead of reaching for the salt shaker are effective
        strategies for reducing both sodium and microplastic intake simultaneously.
      </p>

      {/* MicroPlastics App */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How the MicroPlastics App Helps You Choose Safer Salt
      </h2>
      <p>
        With so many salt brands and types on the market, figuring out which ones carry the lowest
        microplastic risk can be overwhelming. That is exactly the kind of problem the{' '}
        <Link href="/" className="text-[#1E6B47] hover:underline font-medium">
          MicroPlastics app
        </Link>{' '}
        was designed to solve.
      </p>
      <p>
        The app lets you scan any salt product using your phone&apos;s camera or by searching the
        product name. Within seconds, you receive a microplastic exposure score based on the salt
        type, source region, packaging material, and the latest research data. The scoring system
        accounts for whether the product is sea salt, rock salt, or refined, and factors in the
        geographic origin and packaging type to give you a clear, actionable risk assessment.
      </p>
      <p>
        Beyond individual product scans, the app suggests lower-risk alternatives in the same
        product category. If you scan a sea salt and it scores high for microplastic risk, the
        app will recommend specific Himalayan or rock salt options that score lower. This makes
        it practical to make better choices right at the point of purchase, without needing to
        memorize research studies or brand comparisons.
      </p>
      <p>
        The app also tracks your cumulative microplastic exposure over time, so you can see how
        switching salt brands -- along with other changes in your diet and product choices --
        reduces your overall intake. It is free to download with 3 scans included, and
        subscription plans unlock unlimited scanning along with detailed breakdowns and
        personalized recommendations.
      </p>

      {/* Conclusion */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line
      </h2>
      <p>
        Microplastics in salt are a real and well-documented phenomenon. The global research is
        clear: 90% of sea salt brands contain microplastics, with contamination levels varying
        widely based on salt type and geographic origin. Sea salt carries the highest load, lake
        salt falls in the middle, and mined rock salt and Himalayan salt are the cleanest options
        available.
      </p>
      <p>
        While the microplastic dose from salt alone represents a small fraction of your total
        daily exposure, it is one of the easiest sources to reduce. Simply switching from sea salt
        to Himalayan or rock salt, reducing your overall salt intake to healthier levels, and
        storing salt in glass rather than plastic containers are practical changes that anyone can
        make today.
      </p>
      <p>
        Combined with other dietary adjustments -- filtering your water, choosing glass-packaged
        products, and being mindful of highly contaminated food categories -- switching your salt
        is one piece of a larger strategy for reducing your body&apos;s microplastic burden. Every
        small change adds up, and the research consistently shows that informed consumer choices
        can make a meaningful difference.
      </p>
      <p>
        <strong>Ready to find out what is in your salt?</strong> Download the{' '}
        <Link href="/" className="text-[#1E6B47] hover:underline font-medium">
          MicroPlastics app
        </Link>{' '}
        and scan the salt brands in your kitchen. Knowledge is the first step toward a
        lower-plastic life.
      </p>

      {/* Related Articles */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Related Articles
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <Link href="/microplastics-in-food" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Food: Which Foods Contain the Most?
          </Link>
        </li>
        <li>
          <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
            How to Avoid Microplastics: The Complete 2025 Guide
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:underline font-medium">
            Microplastics in Bottled Water: Why You Should Switch to Filtered Tap
          </Link>
        </li>
        <li>
          <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
            The Health Effects of Microplastics: What Science Says
          </Link>
        </li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Does sea salt contain microplastics?',
            answer:
              'Yes. Studies have found up to 600 microplastic particles per kilogram of sea salt. The contamination is highest in Mediterranean and Asian sea salts, lower in salts from less-polluted oceans. Polyethylene and polypropylene are the dominant polymers detected.',
          },
          {
            question: 'Is Himalayan salt safer than sea salt?',
            answer:
              'Yes. Pink Himalayan salt and other mined rock salts are extracted from ancient salt deposits that predate plastic pollution by millions of years. Particle counts in mined salts are typically a small fraction of those in sea salt.',
          },
          {
            question: 'Does iodized table salt contain microplastics?',
            answer:
              'Most iodized table salt is mined rock salt with iodine added during processing. So the iodized version often has less microplastic than evaporated sea salt. The plastic packaging it ships in can add a small amount; transfer to a glass shaker if concerned.',
          },
          {
            question: 'Should I switch from sea salt to rock salt?',
            answer:
              'For daily cooking use, yes, pink Himalayan salt or kosher rock salt is cleaner than sea salt with no nutritional downside. For occasional flaky finishing salts (Maldon, fleur de sel), the small amount used means the exposure difference is minor.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'University of Amsterdam research group',
            year: 2026,
            title: 'Microplastic intake across 13 food categories in the human diet',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://www.sciencedirect.com/science/article/pii/S2772502225006602',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human Consumption of Microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2017,
            title: 'The presence of microplastics in commercial salts from different countries',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/srep46173',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
